import type {
  GraphMember,
} from '@/contexts/FamilyGraphContext';

import {
  PROVISIONAL_SUBJECT_ENDPOINT_TOKEN,
  type CanonicalTreeEndpointFactPlannerResult,
  type CanonicalTreeEndpointReference,
} from './CanonicalTreeEndpointFactAuthoringPlanner';

export type CanonicalTreeSubjectIdentityResolutionStatus =
  | 'REUSE_EXISTING'
  | 'CREATE_NEW_EXPLICIT'
  | 'REQUIRES_IDENTITY_DECISION'
  | 'BLOCKED';

export type CanonicalTreeSubjectIdentityResolution =
  | Readonly<{
      kind: 'reuse-existing';
      memberId: string;
    }>
  | Readonly<{
      kind: 'create-new-intent';
      token:
        typeof PROVISIONAL_SUBJECT_ENDPOINT_TOKEN;
    }>
  | null;

export interface CanonicalTreeSubjectIdentityResolutionInput {
  readonly members:
    readonly GraphMember[];

  readonly viewerMemberId:
    string | null;

  readonly plannerResult:
    CanonicalTreeEndpointFactPlannerResult;

  readonly selectedExistingMemberId?:
    string | null;

  readonly createNewExplicit?:
    boolean;

  /*
   * Read-only advisory candidate ids supplied by a
   * separately governed discovery surface.
   *
   * They may be displayed as warnings only.
   */
  readonly similarityWarningMemberIds?:
    readonly string[];
}

export interface CanonicalTreeSubjectIdentityResolutionResult {
  readonly status:
    CanonicalTreeSubjectIdentityResolutionStatus;

  readonly subjectResolution:
    CanonicalTreeSubjectIdentityResolution;

  readonly similarityWarningMemberIds:
    readonly string[];

  readonly blockers:
    readonly string[];

  readonly decisionExplicit:
    boolean;

  readonly ceremonyComplete:
    boolean;

  readonly mutatesCanonicalGraph:
    false;

  readonly databaseActionPerformed:
    false;

  readonly personCreated:
    false;

  readonly canonicalMemberIdMinted:
    false;

  readonly claimBindingPerformed:
    false;

  readonly claimRebindPerformed:
    false;

  readonly mergePerformed:
    false;

  readonly identityAuthorityInferredFromSimilarity:
    false;

  readonly executionAuthorized:
    false;

  readonly writeAuthorized:
    false;

  readonly transactionAuthorized:
    false;
}

const activeCanonicalMember = (
  member: GraphMember,
): boolean =>
  member.approvalStatus !==
    'rejected' &&
  (
    member.profileState as string
  ) !==
    'deleted';

const sortedUnique = (
  values:
    readonly string[],
): readonly string[] =>
  Object.freeze(
    Array.from(
      new Set(
        values
          .map(
            value =>
              value.trim(),
          )
          .filter(
            Boolean,
          ),
      ),
    ).sort(),
  );

const provisionalEndpoint = (
  endpoint:
    CanonicalTreeEndpointReference,
): boolean =>
  endpoint.kind ===
    'provisional-subject' &&
  endpoint.token ===
    PROVISIONAL_SUBJECT_ENDPOINT_TOKEN;

const planContainsProvisionalSubject = (
  plan:
    CanonicalTreeEndpointFactPlannerResult,
): boolean =>
  plan.factProposals.some(
    proposal =>
      provisionalEndpoint(
        proposal.source,
      ) ||
      provisionalEndpoint(
        proposal.target,
      ),
  );

const immutableResolution = (
  resolution:
    CanonicalTreeSubjectIdentityResolution,
): CanonicalTreeSubjectIdentityResolution =>
  resolution ===
    null
    ? null
    : Object.freeze({
        ...resolution,
      });

const result = (
  status:
    CanonicalTreeSubjectIdentityResolutionStatus,
  subjectResolution:
    CanonicalTreeSubjectIdentityResolution,
  similarityWarningMemberIds:
    readonly string[],
  blockers:
    readonly string[],
  decisionExplicit:
    boolean,
  ceremonyComplete:
    boolean,
): CanonicalTreeSubjectIdentityResolutionResult =>
  Object.freeze({
    status,

    subjectResolution:
      immutableResolution(
        subjectResolution,
      ),

    similarityWarningMemberIds:
      sortedUnique(
        similarityWarningMemberIds,
      ),

    blockers:
      sortedUnique(
        blockers,
      ),

    decisionExplicit,

    ceremonyComplete,

    mutatesCanonicalGraph:
      false,

    databaseActionPerformed:
      false,

    personCreated:
      false,

    canonicalMemberIdMinted:
      false,

    claimBindingPerformed:
      false,

    claimRebindPerformed:
      false,

    mergePerformed:
      false,

    identityAuthorityInferredFromSimilarity:
      false,

    executionAuthorized:
      false,

    writeAuthorized:
      false,

    transactionAuthorized:
      false,
  });

const blocked = (
  blocker: string,
): CanonicalTreeSubjectIdentityResolutionResult =>
  result(
    'BLOCKED',
    null,
    [],
    [
      blocker,
    ],
    false,
    false,
  );

/*
 * CC-TREE-LIVE-003G3WC GREEN v1.
 *
 * This is a pure identity-decision ceremony.
 *
 * It decides only whether provisional SUBJECT should:
 * - reuse one explicitly selected active canonical member; or
 * - proceed as explicit create-new intent.
 *
 * Similarity never decides identity.
 *
 * This authority creates nothing, binds nothing, merges
 * nothing, writes nothing, and grants no transaction or
 * execution authority.
 */
export const evaluateCanonicalTreeSubjectIdentityResolutionCeremony = (
  input:
    CanonicalTreeSubjectIdentityResolutionInput,
): CanonicalTreeSubjectIdentityResolutionResult => {
  /*
   * Stage 1 — B plan authority.
   */
  if (
    input.plannerResult.status !==
      'READY_FOR_IDENTITY_RESOLUTION'
  ) {
    return blocked(
      'B_PLAN_NOT_READY_FOR_IDENTITY_RESOLUTION',
    );
  }

  if (
    input.plannerResult
      .subjectIdentityResolutionRequired !==
      true ||
    input.plannerResult
      .identityResolutionPerformed !==
      false ||
    !planContainsProvisionalSubject(
      input.plannerResult,
    )
  ) {
    return blocked(
      'B_PLAN_DOES_NOT_REQUIRE_PROVISIONAL_SUBJECT_RESOLUTION',
    );
  }

  /*
   * Stage 2 — exact viewer authority.
   */
  if (
    !input.viewerMemberId
  ) {
    return blocked(
      'MISSING_CANONICAL_VIEWER',
    );
  }

  const viewer =
    input.members.find(
      member =>
        member.id ===
        input.viewerMemberId,
    ) ??
    null;

  if (
    !viewer ||
    !activeCanonicalMember(
      viewer,
    )
  ) {
    return blocked(
      'STALE_CANONICAL_VIEWER',
    );
  }

  /*
   * Stage 3 — mutually exclusive explicit decisions.
   */
  const selectedExistingMemberId =
    (
      input.selectedExistingMemberId ??
      ''
    ).trim();

  const reuseExisting =
    selectedExistingMemberId.length >
      0;

  const createNewExplicit =
    input.createNewExplicit ===
      true;

  if (
    reuseExisting &&
    createNewExplicit
  ) {
    return blocked(
      'CONTRADICTORY_IDENTITY_DECISION',
    );
  }

  /*
   * Stage 4 — explicit existing-person validation.
   */
  let selectedExisting:
    GraphMember |
    null =
      null;

  if (
    reuseExisting
  ) {
    selectedExisting =
      input.members.find(
        member =>
          member.id ===
          selectedExistingMemberId,
      ) ??
      null;

    if (
      !selectedExisting
    ) {
      return blocked(
        'STALE_SELECTED_EXISTING_MEMBER',
      );
    }

    if (
      !activeCanonicalMember(
        selectedExisting,
      )
    ) {
      return blocked(
        'REJECTED_SELECTED_EXISTING_MEMBER',
      );
    }

    if (
      selectedExisting.ownerUserId !==
      viewer.ownerUserId
    ) {
      return blocked(
        'CROSS_GRAPH_SELECTED_EXISTING_MEMBER',
      );
    }
  }

  /*
   * Stage 5 — advisory similarity normalization.
   *
   * No ranking or confidence value can change the
   * identity decision.
   */
  const similarityWarnings =
    sortedUnique(
      input.similarityWarningMemberIds ??
      [],
    );

  /*
   * Stage 6 — explicit decision finalization.
   */
  if (
    selectedExisting
  ) {
    return result(
      'REUSE_EXISTING',
      Object.freeze({
        kind:
          'reuse-existing' as const,

        memberId:
          selectedExisting.id,
      }),
      similarityWarnings,
      [],
      true,
      true,
    );
  }

  if (
    createNewExplicit
  ) {
    return result(
      'CREATE_NEW_EXPLICIT',
      Object.freeze({
        kind:
          'create-new-intent' as const,

        token:
          PROVISIONAL_SUBJECT_ENDPOINT_TOKEN,
      }),
      similarityWarnings,
      [],
      true,
      true,
    );
  }

  /*
   * Stage 6 — no explicit decision.
   */
  return result(
    'REQUIRES_IDENTITY_DECISION',
    null,
    similarityWarnings,
    [],
    false,
    false,
  );
};
